import dignity from 'dignity.js';
import { gameContractHash, sha512 } from './certificates.js';

const { VDF } = dignity;

export const TIME_WEAVE_MOVE_INTERVAL = 4;
export const MIN_VDF_STEPS_PER_SECOND = 1;
export const CALIBRATION_STORAGE_KEY = 'dechess:vdf-calibration:v1';

export async function calibrateVdf({ sampleSteps = 2 } = {}) {
  const challenge = await sha512(`dechess-vdf-calibration:${crypto.randomUUID()}`);
  const startedAt = performance.now();
  await VDF.compute(challenge, BigInt(sampleSteps));
  const elapsedMs = Math.max(1, performance.now() - startedAt);
  return {
    measuredAt: Date.now(),
    sampleSteps,
    elapsedMs,
    stepsPerSecond: Math.max(
      MIN_VDF_STEPS_PER_SECOND,
      (sampleSteps * 1000) / elapsedMs
    )
  };
}

export async function loadOrCalibrateVdf() {
  const saved = localStorage.getItem(CALIBRATION_STORAGE_KEY);
  if (saved) {
    try {
      const calibration = JSON.parse(saved);
      if (
        calibration.stepsPerSecond >= MIN_VDF_STEPS_PER_SECOND
        && Date.now() - calibration.measuredAt < 30 * 24 * 60 * 60 * 1000
      ) {
        return calibration;
      }
    } catch {
      // Recalibrate malformed or stale values.
    }
  }
  const calibration = await calibrateVdf();
  localStorage.setItem(CALIBRATION_STORAGE_KEY, JSON.stringify(calibration));
  return calibration;
}

function checkpointMoves(game, moveInterval) {
  const checkpoints = [];
  for (let index = moveInterval - 1; index < game.moves.length; index += moveInterval) {
    checkpoints.push(game.moves[index]);
  }
  const finalMove = game.moves.at(-1);
  if (finalMove && checkpoints.at(-1)?.ply !== finalMove.ply) {
    checkpoints.push(finalMove);
  }
  if (checkpoints.length === 0) {
    checkpoints.push(null);
  }
  return checkpoints;
}

export async function computeTimeWeaveProof({
  game,
  playerId,
  calibration,
  stepsPerCheckpoint = 1,
  moveInterval = TIME_WEAVE_MOVE_INTERVAL,
  compute = (challenge, steps) => VDF.compute(challenge, BigInt(steps))
}) {
  if (!game.players.white || !game.players.black) {
    throw new Error('Time-weave requires two players');
  }
  if (![game.players.white.playerId, game.players.black.playerId].includes(playerId)) {
    throw new Error('Time-weave prover is not a player');
  }
  const steps = Math.max(1, Math.floor(stepsPerCheckpoint));
  const seed = await gameContractHash(game);
  let currentSeed = seed;
  const checkpoints = [];

  for (const move of checkpointMoves(game, moveInterval)) {
    const output = await compute(currentSeed, steps);
    const checkpoint = {
      inputSeed: currentSeed,
      output,
      steps,
      movePly: move?.ply || null,
      moveSignature: move?.signature || null
    };
    checkpoints.push(checkpoint);
    currentSeed = move?.signature
      ? await sha512({
        vdfCheckpoint: output,
        moveSignature: move.signature
      })
      : output;
  }

  return {
    schemaVersion: 1,
    playerId,
    gameId: game.id,
    contractSeed: seed,
    calibration: {
      measuredAt: calibration.measuredAt,
      stepsPerSecond: Math.max(
        MIN_VDF_STEPS_PER_SECOND,
        calibration.stepsPerSecond
      )
    },
    moveInterval,
    totalSteps: checkpoints.reduce((sum, checkpoint) => sum + checkpoint.steps, 0),
    checkpoints,
    finalOutput: currentSeed
  };
}

export async function verifyTimeWeaveProof(proof, game, expectedPlayerId) {
  const errors = [];
  if (proof?.schemaVersion !== 1) {
    errors.push('unsupported-schema');
  }
  if (proof?.playerId !== expectedPlayerId) {
    errors.push('player-mismatch');
  }
  if (proof?.gameId !== game.id) {
    errors.push('game-mismatch');
  }
  const contractSeed = await gameContractHash(game);
  if (proof?.contractSeed !== contractSeed) {
    errors.push('contract-seed-mismatch');
  }
  if (
    !Number.isFinite(proof?.calibration?.stepsPerSecond)
    || proof.calibration.stepsPerSecond < MIN_VDF_STEPS_PER_SECOND
  ) {
    errors.push('invalid-calibration');
  }

  let currentSeed = contractSeed;
  let totalSteps = 0;
  for (const checkpoint of proof?.checkpoints || []) {
    if (checkpoint.inputSeed !== currentSeed) {
      errors.push('checkpoint-chain-mismatch');
      break;
    }
    if (!Number.isInteger(checkpoint.steps) || checkpoint.steps < 1) {
      errors.push('invalid-checkpoint-steps');
      break;
    }
    if (!(await VDF.verify(currentSeed, BigInt(checkpoint.steps), checkpoint.output))) {
      errors.push('invalid-checkpoint-output');
      break;
    }
    if (checkpoint.movePly) {
      const move = game.moves.find(({ ply }) => ply === checkpoint.movePly);
      if (!move || move.signature !== checkpoint.moveSignature) {
        errors.push('move-signature-mismatch');
        break;
      }
      currentSeed = await sha512({
        vdfCheckpoint: checkpoint.output,
        moveSignature: checkpoint.moveSignature
      });
    } else {
      currentSeed = checkpoint.output;
    }
    totalSteps += checkpoint.steps;
  }

  if (totalSteps !== proof?.totalSteps) {
    errors.push('total-steps-mismatch');
  }
  if (currentSeed !== proof?.finalOutput) {
    errors.push('final-output-mismatch');
  }
  const effectiveRate = Math.max(
    MIN_VDF_STEPS_PER_SECOND,
    proof?.calibration?.stepsPerSecond || 0
  );
  return {
    valid: errors.length === 0,
    errors,
    verifiedDurationMs: (totalSteps / effectiveRate) * 1000
  };
}

export async function verifyDetachedTimeWeaveProof(
  proof,
  { gameId, contractHash, playerId }
) {
  const errors = [];
  if (proof?.schemaVersion !== 1) {
    errors.push('unsupported-schema');
  }
  if (proof?.playerId !== playerId) {
    errors.push('player-mismatch');
  }
  if (proof?.gameId !== gameId) {
    errors.push('game-mismatch');
  }
  if (proof?.contractSeed !== contractHash) {
    errors.push('contract-seed-mismatch');
  }
  if (
    !Number.isFinite(proof?.calibration?.stepsPerSecond)
    || proof.calibration.stepsPerSecond < MIN_VDF_STEPS_PER_SECOND
  ) {
    errors.push('invalid-calibration');
  }

  let currentSeed = contractHash;
  let totalSteps = 0;
  for (const checkpoint of proof?.checkpoints || []) {
    if (
      checkpoint.inputSeed !== currentSeed
      || !Number.isInteger(checkpoint.steps)
      || checkpoint.steps < 1
    ) {
      errors.push('invalid-checkpoint-chain');
      break;
    }
    if (!(await VDF.verify(currentSeed, BigInt(checkpoint.steps), checkpoint.output))) {
      errors.push('invalid-checkpoint-output');
      break;
    }
    currentSeed = checkpoint.moveSignature
      ? await sha512({
        vdfCheckpoint: checkpoint.output,
        moveSignature: checkpoint.moveSignature
      })
      : checkpoint.output;
    totalSteps += checkpoint.steps;
  }
  if (totalSteps !== proof?.totalSteps) {
    errors.push('total-steps-mismatch');
  }
  if (currentSeed !== proof?.finalOutput) {
    errors.push('final-output-mismatch');
  }
  return {
    valid: errors.length === 0,
    errors,
    verifiedDurationMs:
      (totalSteps / Math.max(
        MIN_VDF_STEPS_PER_SECOND,
        proof?.calibration?.stepsPerSecond || 0
      )) * 1000
  };
}

export function createVdfWorker() {
  return new Worker(new URL('./timeWeave.worker.js', import.meta.url), {
    type: 'module'
  });
}

export function computeVdfInWorker(worker, challengeHex, steps) {
  return new Promise((resolve, reject) => {
    if (worker?.__dechessTerminated) {
      reject(new Error('VDF worker terminated'));
      return;
    }
    const id = crypto.randomUUID();
    const handleMessage = ({ data }) => {
      if (data.id !== id) {
        return;
      }
      worker.removeEventListener('message', handleMessage);
      if (data.ok) {
        resolve(data.output);
      } else {
        reject(new Error(data.error));
      }
    };
    const handleError = () => {
      worker.removeEventListener('message', handleMessage);
      reject(new Error('VDF worker failed'));
    };
    worker.addEventListener('message', handleMessage);
    worker.addEventListener('error', handleError, { once: true });
    try {
      worker.postMessage({
        id,
        type: 'compute',
        payload: { challengeHex, steps }
      });
    } catch (error) {
      worker.removeEventListener('message', handleMessage);
      reject(error instanceof Error ? error : new Error(String(error)));
    }
  });
}

function terminateVdfWorker(worker, pendingReject = null) {
  if (!worker || worker.__dechessTerminated) {
    pendingReject?.(new Error('VDF worker terminated'));
    return;
  }
  worker.__dechessTerminated = true;
  pendingReject?.(new Error('VDF worker terminated'));
  try {
    worker.terminate();
  } catch {
    // Ignore double-terminate.
  }
}

export async function createLiveTimeWeave({
  game,
  playerId,
  calibration,
  worker = createVdfWorker(),
  chunkTargetMs = 1000,
  yieldMs = 50
}) {
  const contractSeed = await gameContractHash(game);
  const effectiveCalibration = {
    measuredAt: calibration.measuredAt,
    stepsPerSecond: Math.max(
      MIN_VDF_STEPS_PER_SECOND,
      calibration.stepsPerSecond
    )
  };
  const stepsPerChunk = Math.max(
    1,
    Math.floor(effectiveCalibration.stepsPerSecond * (chunkTargetMs / 1000))
  );
  let currentSeed = contractSeed;
  let segmentInputSeed = contractSeed;
  let segmentSteps = 0;
  let stopping = false;
  let settled = false;
  let pendingReject = null;
  const queuedMoves = [];
  const queuedPlies = new Set();
  const checkpoints = [];

  function queueMove(move) {
    if (move && !queuedPlies.has(move.ply)) {
      queuedPlies.add(move.ply);
      queuedMoves.push(move);
    }
  }

  const completion = (async () => {
    try {
      while (!stopping || queuedMoves.length > 0) {
        const output = await new Promise((resolve, reject) => {
          pendingReject = reject;
          computeVdfInWorker(worker, currentSeed, stepsPerChunk)
            .then(resolve, reject)
            .finally(() => {
              if (pendingReject === reject) {
                pendingReject = null;
              }
            });
        });
        segmentSteps += stepsPerChunk;
        const move = queuedMoves.shift() || null;

        if (move?.signature) {
          checkpoints.push({
            inputSeed: segmentInputSeed,
            output,
            steps: segmentSteps,
            movePly: move.ply,
            moveSignature: move.signature
          });
          currentSeed = await sha512({
            vdfCheckpoint: output,
            moveSignature: move.signature
          });
          segmentInputSeed = currentSeed;
          segmentSteps = 0;
        } else {
          currentSeed = output;
        }

        if (yieldMs > 0 && (!stopping || queuedMoves.length > 0)) {
          await new Promise((resolve) => setTimeout(resolve, yieldMs));
        }
      }

      if (segmentSteps > 0) {
        checkpoints.push({
          inputSeed: segmentInputSeed,
          output: currentSeed,
          steps: segmentSteps,
          movePly: null,
          moveSignature: null
        });
      }
      settled = true;
    } catch (error) {
      if (stopping && /terminated/i.test(String(error?.message || error))) {
        settled = true;
        return;
      }
      throw error;
    } finally {
      terminateVdfWorker(worker, pendingReject);
      pendingReject = null;
    }
  })();

  return {
    observeGame(nextGame) {
      nextGame.moves
        .filter((move) => move.ply % TIME_WEAVE_MOVE_INTERVAL === 0)
        .forEach(queueMove);
    },
    async finalize(nextGame) {
      const finalMove = nextGame.moves.at(-1);
      queueMove(finalMove);
      stopping = true;
      await completion;
      return {
        schemaVersion: 1,
        playerId,
        gameId: game.id,
        contractSeed,
        calibration: effectiveCalibration,
        moveInterval: TIME_WEAVE_MOVE_INTERVAL,
        totalSteps: checkpoints.reduce(
          (sum, checkpoint) => sum + checkpoint.steps,
          0
        ),
        checkpoints,
        finalOutput: currentSeed
      };
    },
    cancel() {
      queuedMoves.length = 0;
      stopping = true;
      terminateVdfWorker(worker, pendingReject);
      pendingReject = null;
    },
    get settled() {
      return settled;
    }
  };
}
